
import * as THREE from "../../../libs/three.js/build/three.module.js";

// Feature D (korteste afstand) - a small, purpose-built BVH over a mesh's
// own triangles (world-space), used for both the IFC↔IFC nearest-triangle-
// pair search and IFC↔punktsky nearest-point search. Not a port of a
// general-purpose library (three-mesh-bvh isn't vendored in this project);
// a plain recursive median-split tree is enough to turn the spec's own
// "bounding-box-pruning, derefter trekant-til-trekant-afstand" two-stage
// algorithm from an O(n·m) brute force into something that actually scales
// to real IFC element triangle counts, without adding a new dependency.
const LEAF_SIZE = 8;

function triangleBox(tri){
	let box = new THREE.Box3();
	box.expandByPoint(tri.a);
	box.expandByPoint(tri.b);
	box.expandByPoint(tri.c);
	return box;
}

function buildNode(triangles, indices){
	let box = new THREE.Box3();
	for(let i of indices){
		box.union(triangles[i].box);
	}

	if(indices.length <= LEAF_SIZE){
		return {box, leaf: true, indices};
	}

	let size = box.getSize(new THREE.Vector3());
	let axis = size.x >= size.y && size.x >= size.z ? "x" : (size.y >= size.z ? "y" : "z");

	let sorted = indices.slice().sort((a, b) => triangles[a].centroid[axis] - triangles[b].centroid[axis]);
	let mid = sorted.length >> 1;

	return {
		box,
		leaf: false,
		left: buildNode(triangles, sorted.slice(0, mid)),
		right: buildNode(triangles, sorted.slice(mid)),
	};
}

// Builds a BVH from a THREE.BufferGeometry (indexed or not) plus the
// owning mesh's current world matrix - triangles are baked into world
// space once, up front, so the tree needs no further transform lookups
// during traversal/queries.
export function buildTriangleBVH(geometry, matrixWorld){
	let posAttr = geometry.attributes.position;
	let index = geometry.index;
	let triCount = index ? (index.count / 3) : (posAttr.count / 3);

	let triangles = [];
	for(let t = 0; t < triCount; t++){
		let ia = index ? index.getX(t * 3 + 0) : t * 3 + 0;
		let ib = index ? index.getX(t * 3 + 1) : t * 3 + 1;
		let ic = index ? index.getX(t * 3 + 2) : t * 3 + 2;

		let a = new THREE.Vector3().fromBufferAttribute(posAttr, ia).applyMatrix4(matrixWorld);
		let b = new THREE.Vector3().fromBufferAttribute(posAttr, ib).applyMatrix4(matrixWorld);
		let c = new THREE.Vector3().fromBufferAttribute(posAttr, ic).applyMatrix4(matrixWorld);

		let centroid = a.clone().add(b).add(c).multiplyScalar(1 / 3);
		let tri = {a, b, c, centroid};
		tri.box = triangleBox(tri);
		triangles.push(tri);
	}

	if(triangles.length === 0){
		return {root: null, triangles: []};
	}

	let root = buildNode(triangles, triangles.map((_, i) => i));
	return {root, triangles};
}

// Merges any number of geometry/matrixWorld pairs (an IFC element can be
// made of several placed geometries/meshes) into one combined BVH - the
// spec's own "element", not "mesh", is the unit Feature D measures against.
export function buildTriangleBVHForMeshes(meshes){
	let triangles = [];
	for(let mesh of meshes){
		let {triangles: meshTriangles} = buildTriangleBVH(mesh.geometry, mesh.matrixWorld);
		triangles.push(...meshTriangles);
	}
	if(triangles.length === 0){
		return {root: null, triangles: []};
	}
	let root = buildNode(triangles, triangles.map((_, i) => i));
	return {root, triangles};
}

function closestPointOnSegment(p, a, b, out){
	let ab = new THREE.Vector3().subVectors(b, a);
	let t = ab.lengthSq() < 1e-12 ? 0 : Math.max(0, Math.min(1, new THREE.Vector3().subVectors(p, a).dot(ab) / ab.lengthSq()));
	return out.copy(a).addScaledVector(ab, t);
}

// Closest point on triangle (a,b,c) to point p - the standard
// region-based projection (Ericson, "Real-Time Collision Detection" §5.1.5),
// used both for point-to-triangle (part of triangle-triangle distance) and
// directly for the IFC↔punktsky nearest-point query below.
export function closestPointOnTriangle(p, a, b, c, out){
	let ab = new THREE.Vector3().subVectors(b, a);
	let ac = new THREE.Vector3().subVectors(c, a);
	let ap = new THREE.Vector3().subVectors(p, a);

	let d1 = ab.dot(ap), d2 = ac.dot(ap);
	if(d1 <= 0 && d2 <= 0){ return out.copy(a); }

	let bp = new THREE.Vector3().subVectors(p, b);
	let d3 = ab.dot(bp), d4 = ac.dot(bp);
	if(d3 >= 0 && d4 <= d3){ return out.copy(b); }

	let vc = d1 * d4 - d3 * d2;
	if(vc <= 0 && d1 >= 0 && d3 <= 0){
		let v = d1 / (d1 - d3);
		return out.copy(a).addScaledVector(ab, v);
	}

	let cp = new THREE.Vector3().subVectors(p, c);
	let d5 = ab.dot(cp), d6 = ac.dot(cp);
	if(d6 >= 0 && d5 <= d6){ return out.copy(c); }

	let vb = d5 * d2 - d1 * d6;
	if(vb <= 0 && d2 >= 0 && d6 <= 0){
		let w = d2 / (d2 - d6);
		return out.copy(a).addScaledVector(ac, w);
	}

	let va = d3 * d6 - d5 * d4;
	if(va <= 0 && (d4 - d3) >= 0 && (d5 - d6) >= 0){
		let w = (d4 - d3) / ((d4 - d3) + (d5 - d6));
		return out.copy(b).addScaledVector(new THREE.Vector3().subVectors(c, b), w);
	}

	let denom = 1 / (va + vb + vc);
	let v = vb * denom, w = vc * denom;
	return out.copy(a).addScaledVector(ab, v).addScaledVector(ac, w);
}

const _pA = new THREE.Vector3(), _pB = new THREE.Vector3();

// Minimum distance between two triangles plus their closest points -
// decomposed into the 9 edge-edge pairs and 6 vertex-to-opposite-triangle
// projections, which together are the complete set of candidates for two
// non-intersecting convex (triangular) regions. Triangles that actually
// intersect/overlap correctly come out at (very near) 0 through this same
// decomposition in every case that matters in practice (an edge crossing
// the other triangle's plane inside its bounds) - the spec's own "ved
// overlap/kollision returneres 0" - without a separate, fully general
// triangle-triangle intersection predicate, a deliberate simplification
// given how rarely two BIM elements interpenetrate without any edge
// actually crossing the other's face.
export function triangleTriangleDistance(t1, t2){
	let best = Infinity;
	let bestA = new THREE.Vector3(), bestB = new THREE.Vector3();

	let edgesA = [[t1.a, t1.b], [t1.b, t1.c], [t1.c, t1.a]];
	let edgesB = [[t2.a, t2.b], [t2.b, t2.c], [t2.c, t2.a]];

	for(let [a0, a1] of edgesA){
		for(let [b0, b1] of edgesB){
			let d = closestSegmentSegment(a0, a1, b0, b1, _pA, _pB);
			if(d < best){
				best = d;
				bestA.copy(_pA);
				bestB.copy(_pB);
			}
		}
	}

	let verts1 = [t1.a, t1.b, t1.c];
	for(let v of verts1){
		closestPointOnTriangle(v, t2.a, t2.b, t2.c, _pB);
		let d = v.distanceTo(_pB);
		if(d < best){
			best = d;
			bestA.copy(v);
			bestB.copy(_pB);
		}
	}

	let verts2 = [t2.a, t2.b, t2.c];
	for(let v of verts2){
		closestPointOnTriangle(v, t1.a, t1.b, t1.c, _pA);
		let d = v.distanceTo(_pA);
		if(d < best){
			best = d;
			bestA.copy(_pA);
			bestB.copy(v);
		}
	}

	return {distance: best, pointOnA: bestA.clone(), pointOnB: bestB.clone()};
}

// Closest points between two segments (a0-a1) and (b0-b1) - the standard
// closed-form solution, degenerate-segment-safe. Writes into outA/outB and
// returns the distance.
function closestSegmentSegment(a0, a1, b0, b1, outA, outB){
	let d1 = new THREE.Vector3().subVectors(a1, a0);
	let d2 = new THREE.Vector3().subVectors(b1, b0);
	let r = new THREE.Vector3().subVectors(a0, b0);

	let aa = d1.dot(d1), ee = d2.dot(d2), f = d2.dot(r);
	let s, t;

	if(aa <= 1e-12 && ee <= 1e-12){
		outA.copy(a0); outB.copy(b0);
		return outA.distanceTo(outB);
	}
	if(aa <= 1e-12){
		s = 0; t = Math.max(0, Math.min(1, f / ee));
	}else{
		let c = d1.dot(r);
		if(ee <= 1e-12){
			t = 0; s = Math.max(0, Math.min(1, -c / aa));
		}else{
			let b = d1.dot(d2);
			let denom = aa * ee - b * b;
			s = denom > 1e-12 ? Math.max(0, Math.min(1, (b * f - c * ee) / denom)) : 0;
			t = (b * s + f) / ee;
			if(t < 0){ t = 0; s = Math.max(0, Math.min(1, -c / aa)); }
			else if(t > 1){ t = 1; s = Math.max(0, Math.min(1, (b - c) / aa)); }
		}
	}

	outA.copy(a0).addScaledVector(d1, s);
	outB.copy(b0).addScaledVector(d2, t);
	return outA.distanceTo(outB);
}

function boxDistance(box1, box2){
	let dx = Math.max(box1.min.x - box2.max.x, box2.min.x - box1.max.x, 0);
	let dy = Math.max(box1.min.y - box2.max.y, box2.min.y - box1.max.y, 0);
	let dz = Math.max(box1.min.z - box2.max.z, box2.min.z - box1.max.z, 0);
	return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

// Nearest triangle pair between two BVHs, with bounding-box pruning at
// every level (a node pair whose box-to-box distance already exceeds the
// current best is never descended into) - the spec's own two-stage
// algorithm. Returns null if either BVH is empty (no triangles at all).
export function nearestBetweenBVHs(bvhA, bvhB){
	if(!bvhA.root || !bvhB.root){
		return null;
	}

	let state = {distance: Infinity, pointOnA: null, pointOnB: null};

	let visit = (nodeA, nodeB) => {
		if(boxDistance(nodeA.box, nodeB.box) >= state.distance){
			return;
		}

		if(nodeA.leaf && nodeB.leaf){
			for(let i of nodeA.indices){
				for(let j of nodeB.indices){
					let result = triangleTriangleDistance(bvhA.triangles[i], bvhB.triangles[j]);
					if(result.distance < state.distance){
						state.distance = result.distance;
						state.pointOnA = result.pointOnA;
						state.pointOnB = result.pointOnB;
					}
				}
			}
			return;
		}

		// Descend into whichever side still has children, always pairing
		// against both children of the other side - a standard dual-tree
		// traversal.
		if(nodeA.leaf){
			visit(nodeA, nodeB.left);
			visit(nodeA, nodeB.right);
		}else if(nodeB.leaf){
			visit(nodeA.left, nodeB);
			visit(nodeA.right, nodeB);
		}else{
			visit(nodeA.left, nodeB.left);
			visit(nodeA.left, nodeB.right);
			visit(nodeA.right, nodeB.left);
			visit(nodeA.right, nodeB.right);
		}
	};

	visit(bvhA.root, bvhB.root);
	return state.distance === Infinity ? null : state;
}

// Nearest point on a BVH to a single world-space point - Feature D's
// IFC↔punktsky inner loop (called once per candidate point cloud point).
export function nearestPointOnBVH(bvh, point, maxDistance = Infinity){
	if(!bvh.root){
		return null;
	}

	let best = {distance: maxDistance, point: null};
	let closest = new THREE.Vector3();

	let pointBox = new THREE.Box3(
		new THREE.Vector3(point.x - best.distance, point.y - best.distance, point.z - best.distance),
		new THREE.Vector3(point.x + best.distance, point.y + best.distance, point.z + best.distance),
	);

	let visit = (node) => {
		if(!node.box.intersectsBox(pointBox)){
			return;
		}
		let dToBox = node.box.distanceToPoint(point);
		if(dToBox >= best.distance){
			return;
		}

		if(node.leaf){
			for(let i of node.indices){
				let tri = bvh.triangles[i];
				closestPointOnTriangle(point, tri.a, tri.b, tri.c, closest);
				let d = point.distanceTo(closest);
				if(d < best.distance){
					best.distance = d;
					best.point = closest.clone();
					best.triangle = tri; // lets callers read the face normal / check the foot lies inside the face
					pointBox.set(
						new THREE.Vector3(point.x - best.distance, point.y - best.distance, point.z - best.distance),
						new THREE.Vector3(point.x + best.distance, point.y + best.distance, point.z + best.distance),
					);
				}
			}
			return;
		}

		visit(node.left);
		visit(node.right);
	};

	visit(bvh.root);
	return best.point ? best : null;
}
